/**
 * Purchase Order Printing Utility
 * Generates formatted HTML for printing purchase orders
 */

import { toBlob } from "html-to-image";

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "0.00";
  return parseFloat(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format date to readable string
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Extract supplier name from remarks
 * @param {string} remarks - Remarks string
 * @returns {string} Extracted supplier name or 'Not Set'
 */
export const extractSupplierFromRemarks = (remarks) => {
  if (!remarks) return "Not Set";
  const match = remarks.match(/supplier:\s*(.+?)(?:\s*$|\.)/i);
  return match ? match[1] : "Not Set";
};

/**
 * Find supplier by name from suppliers list
 * @param {string} supplierName - Supplier name to search for
 * @param {Array} suppliers - List of suppliers
 * @returns {Object|null} Supplier object or null
 */
export const findSupplierByName = (supplierName, suppliers) => {
  if (!supplierName || supplierName === "Not Set" || !suppliers) return null;

  // Try exact match first
  let supplier = suppliers.find(
    (s) =>
      s.supplier_name &&
      s.supplier_name.toLowerCase() === supplierName.toLowerCase()
  );

  // If no exact match, try partial match
  if (!supplier) {
    supplier = suppliers.find(
      (s) =>
        s.supplier_name &&
        s.supplier_name.toLowerCase().includes(supplierName.toLowerCase())
    );
  }

  return supplier;
};

/**
 * Get winning cost for a canvass item
 * @param {Object} canvassItem - Canvass item object
 * @returns {string} Formatted cost string
 */
export const getWinningCost = (canvassItem) => {
  if (!canvassItem || !canvassItem.winning_supplier) return "0.00";
  const costField = `supplier_${canvassItem.winning_supplier}_cost`;
  const cost = canvassItem[costField];
  return formatCurrency(cost);
};

/**
 * Calculate item total
 * @param {Object} canvassItem - Canvass item object
 * @returns {string} Formatted total string
 */
export const calculateItemTotal = (canvassItem) => {
  if (!canvassItem || !canvassItem.winning_supplier) return "0.00";
  const costField = `supplier_${canvassItem.winning_supplier}_cost`;
  const cost = canvassItem[costField] || 0;
  const qty = canvassItem.qty || 0;
  return formatCurrency(cost * qty);
};

/**
 * Get winning brand/model for a canvass item
 * @param {Object} canvassItem - Canvass item object
 * @returns {string|null} Brand/model or null
 */
export const getWinningBrandModel = (canvassItem) => {
  if (!canvassItem || !canvassItem.winning_supplier) return null;
  const brandModelField = `supplier_${canvassItem.winning_supplier}_item_brand_model`;
  return canvassItem[brandModelField] || null;
};

/**
 * Calculate grand total for purchase order
 * @param {Object} po - Purchase order object
 * @returns {string} Formatted grand total string
 */
export const calculateGrandTotal = (po) => {
  if (!po || !po.items) return "0.00";
  let total = 0;
  po.items.forEach((item) => {
    if (item.canvass_item && item.canvass_item.winning_supplier) {
      const costField = `supplier_${item.canvass_item.winning_supplier}_cost`;
      const cost = item.canvass_item[costField] || 0;
      const qty = item.canvass_item.qty || 0;
      total += cost * qty;
    } else if (item.unit_cost) {
      total += (item.unit_cost || 0) * (item.qty || 0);
    }
  });
  return formatCurrency(total);
};

/**
 * Get branch theme colors based on branch name
 * @param {Object} branch - Branch object
 * @returns {Object} Theme color object
 */
export const getBranchTheme = (branch) => {
  const name = (branch?.branch_name || "").toLowerCase();
  if (name.includes("tandag")) {
    return {
      primary: "#2e7d32",
      primaryDark: "#1b5e20",
      primaryLight: "#e8f5e9",
      primaryMedium: "#c8e6c9",
      gradient: "linear-gradient(to bottom, #2e7d32, #1b5e20)",
      footerGradient: "linear-gradient(to right, #e8f5e9, #a5d6a7)",
      infoBorder: "#2e7d32",
      infoBackground: "linear-gradient(to right, #f1f8e9, #fff)",
    };
  }
  // Default / Cantilan = Blue
  return {
    primary: "#1976d2",
    primaryDark: "#1565c0",
    primaryLight: "#e3f2fd",
    primaryMedium: "#bbdefb",
    gradient: "linear-gradient(to bottom, #1976d2, #1565c0)",
    footerGradient: "linear-gradient(to right, #e3f2fd, #bbdefb)",
    infoBorder: "#1976d2",
    infoBackground: "linear-gradient(to right, #f8f9fa, #fff)",
  };
};

/**
 * Generate print HTML for a purchase order
 * @param {Object} po - Purchase order object
 * @param {Array} suppliers - List of suppliers
 * @param {Object} branch - Branch object with branch_name and branch_address
 * @returns {string} HTML string for printing
 */
export const generatePrintHTML = (
  po,
  suppliers = [],
  branch = null,
  deliveryAddressOverride = "",
  contactPersonOverride = ""
) => {
  const itemsContent = po.items
    .map((item, index) => {
      const isDirectItem = !item.canvass_item;
      const brandModel = item.canvass_item
        ? getWinningBrandModel(item.canvass_item)
        : null;
      const itemName = item.canvass_item
        ? item.canvass_item.item_name
        : item.item_name || "N/A";
      const displayName = brandModel || itemName;
      const qty = item.canvass_item ? item.canvass_item.qty : item.qty || "-";
      const unit = item.canvass_item
        ? item.canvass_item.unit
        : item.unit || "-";
      const unitCost = isDirectItem
        ? formatCurrency(item.unit_cost)
        : getWinningCost(item.canvass_item);
      const totalCost = isDirectItem
        ? formatCurrency((item.qty || 0) * (item.unit_cost || 0))
        : calculateItemTotal(item.canvass_item);
      const itemType = item.canvass_item
        ? item.canvass_item.item_type || "-"
        : "-";

      return `
        <tr>
          <td style="text-align: center;">${index + 1}</td>
          <td>${itemType}</td>
          <td>${displayName}</td>
          <td style="text-align: center;">${qty}</td>
          <td style="text-align: center;">${unit}</td>
          <td style="text-align: right;">${unitCost}</td>
          <td style="text-align: right;">${totalCost}</td>
        </tr>
      `;
    })
    .join("");

  // Extract supplier info from canvass items if no supplier object
  const supplierName =
    po.supplier?.supplier_name || extractSupplierFromRemarks(po.remarks);

  // Find supplier by name to get payment info
  const supplierInfo = findSupplierByName(supplierName, suppliers);

  const bankAccountNumber =
    po.supplier?.bank_account_number || supplierInfo?.bank_account_number || "";
  const bankAccountName =
    po.supplier?.bank_account_name || supplierInfo?.bank_account_name || "";
  const paymentMethod =
    po.supplier?.payment_method || supplierInfo?.payment_method || "";

  const theme = getBranchTheme(branch);
  const branchName = branch?.branch_name || "";
  const branchAddress = branch?.branch_address || "";
  const branchContact = branch?.branch_contact_no || "";
  const branchTin = branch?.branch_tin || "";
  const branchNumber = branch?.branch_number || "";
  const deliveryInformation =
    deliveryAddressOverride || branch?.delivery_information || "";
  const contactPerson = contactPersonOverride || "";

  return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Purchase Order - PO-${String(po.id).padStart(4, "0")}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in;
                size: letter;
              }
              body { margin: 0; }
            }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 20px;
              color: #2c3e50;
              font-size: 12px;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 3px double ${theme.primary};
            }
            .company-name {
              font-size: 24px;
              font-weight: bold;
              color: ${theme.primary};
              letter-spacing: 2px;
              margin-bottom: 3px;
            }
            .document-title {
              font-size: 16px;
              color: #555;
              font-weight: 600;
            }
            .branch-info {
              margin-top: 6px;
              font-size: 12px;
              color: #444;
              line-height: 1.5;
            }
            .branch-name {
              font-weight: 700;
              color: ${theme.primary};
              font-size: 13px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .branch-address {
              font-size: 11px;
              color: #666;
            }
            .branch-contact {
              font-size: 11px;
              color: #666;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
              margin-bottom: 15px;
            }
            .info-item {
              display: flex;
              padding: 8px 12px;
              background: ${theme.infoBackground};
              border-left: 3px solid ${theme.infoBorder};
            }
            .info-label {
              font-weight: 600;
              color: ${theme.primary};
              min-width: 140px;
            }
            .info-value {
              color: #2c3e50;
              flex: 1;
            }
            .section-title {
              font-size: 13px;
              font-weight: 600;
              color: ${theme.primary};
              margin: 15px 0 8px 0;
              padding-bottom: 5px;
              border-bottom: 2px solid ${theme.primaryLight};
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 10px 0;
            }
            th {
              background: ${theme.gradient};
              color: white;
              padding: 8px;
              font-weight: 600;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            td {
              border: 1px solid #e0e0e0;
              padding: 6px 8px;
              font-size: 11px;
            }
            tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            tbody tr:hover {
              background-color: ${theme.primaryLight};
            }
            tfoot td {
              font-weight: bold;
              background: ${theme.footerGradient};
              font-size: 12px;
              padding: 10px 8px;
            }
            .payment-section {
              background: #f8f9fa;
              padding: 12px;
              border-radius: 5px;
              margin: 15px 0;
              border: 1px solid #e0e0e0;
            }
            .payment-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
              margin-top: 8px;
            }
            .payment-field {
              display: flex;
              flex-direction: column;
            }
            .payment-field label {
              font-size: 10px;
              color: #666;
              font-weight: 600;
              margin-bottom: 3px;
            }
            .payment-field .value {
              padding: 6px 8px;
              background: white;
              border: 1px solid #ddd;
              border-radius: 3px;
              min-height: 24px;
              color: #2c3e50;
              font-weight: 500;
            }
            .signature-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              margin-top: 30px;
              page-break-inside: avoid;
            }
            .signature-box {
              text-align: center;
            }
            .signature-line {
              border-bottom: 2px solid #333;
              height: 50px;
              margin-bottom: 8px;
            }
            .signature-label {
              font-weight: 600;
              font-size: 11px;
              margin-bottom: 3px;
            }
            .signature-name {
              color: #666;
              font-size: 10px;
            }
            .footer {
              margin-top: 30px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              text-align: center;
              color: #999;
              font-size: 9px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">JMB COMPUTERS</div>
            <div class="document-title">SUPPLIER PURCHASE ORDER</div>
            ${
              branchName
                ? `
            <div class="branch-info">
              <div class="branch-name">${branchName} Branch</div>
              ${
                branchAddress
                  ? `<div class="branch-address">${branchAddress}</div>`
                  : ""
              }
              ${
                branchContact
                  ? `<div class="branch-contact">Contact: ${branchContact}</div>`
                  : ""
              }
              ${
                branchTin
                  ? `<div class="branch-contact">TIN: ${branchTin}</div>`
                  : ""
              }
              ${
                branchNumber
                  ? `<div class="branch-contact">Branch No: ${branchNumber}</div>`
                  : ""
              }
            </div>
            `
                : ""
            }
          </div>

          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">PO Number:</div>
              <div class="info-value">PO-${String(po.id).padStart(4, "0")}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Date:</div>
              <div class="info-value">${formatDate(po.created_at)}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Supplier:</div>
              <div class="info-value">${supplierName}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Batch Import Code:</div>
              <div class="info-value" style="font-weight: bold;">${
                po.import_batch && po.import_batch.batch_description
                  ? po.import_batch.batch_description
                  : "N/A"
              }</div>
            </div>
            <div class="info-item">
              <div class="info-label">Government PO:</div>
              <div class="info-value">${
                po.canvass && po.canvass.customer_purchase_order
                  ? po.canvass.customer_purchase_order.po_number
                  : po.customer_purchase_order
                  ? po.customer_purchase_order.po_number
                  : "N/A"
              }</div>
            </div>
            <div class="info-item">
              <div class="info-label">Customer Name:</div>
              <div class="info-value">${
                po.canvass &&
                po.canvass.customer_purchase_order &&
                po.canvass.customer_purchase_order.customer
                  ? po.canvass.customer_purchase_order.customer.customer_name
                  : po.customer_purchase_order &&
                    po.customer_purchase_order.customer
                  ? po.customer_purchase_order.customer.customer_name
                  : "N/A"
              }</div>
            </div>
            <div class="info-item">
              <div class="info-label">Status:</div>
              <div class="info-value">${
                po.status
                  ? po.status.charAt(0).toUpperCase() + po.status.slice(1)
                  : "Pending"
              }</div>
            </div>
          </div>

          ${
            po.canvass &&
            po.canvass.purchase_requisition &&
            po.canvass.purchase_requisition.remarks
              ? `
          <div class="info-item" style="grid-column: 1 / -1; margin-bottom: 15px; background: #fff9e6; border-left: 3px solid #ffa726;">
            <div class="info-label" style="color: #f57c00;">PR Remarks:</div>
            <div class="info-value" style="font-style: italic;">${po.canvass.purchase_requisition.remarks}</div>
          </div>
          `
              : ""
          }

          ${
            deliveryInformation || contactPerson
              ? `
          <div class="section-title">Delivery Information</div>
          <div style="display: grid; grid-template-columns: ${
            contactPerson ? "1fr 1fr" : "1fr"
          }; gap: 15px; margin-bottom: 15px;">
            ${
              deliveryInformation
                ? `
            <div class="info-item" style="background: ${theme.infoBackground}; border-left: 3px solid ${theme.infoBorder};">
              <div class="info-label">Deliver To:</div>
              <div class="info-value" style="white-space: pre-line;">${deliveryInformation}</div>
            </div>
            `
                : ""
            }
            ${
              contactPerson
                ? `
            <div class="info-item" style="background: ${theme.infoBackground}; border-left: 3px solid ${theme.infoBorder};">
              <div class="info-label">Contact Person:</div>
              <div class="info-value">${contactPerson}</div>
            </div>
            `
                : ""
            }
          </div>
          `
              : ""
          }

          <div class="section-title">Order Items</div>
          <table>
            <thead>
              <tr>
                <th style="width: 40px;">#</th>
                <th style="width: 100px;">Item Type</th>
                <th>Item Name (Brand/Model)</th>
                <th style="width: 60px;">Qty</th>
                <th style="width: 60px;">Unit</th>
                <th style="width: 100px; text-align: right;">Unit Price</th>
                <th style="width: 100px; text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${itemsContent}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="6" style="text-align: right;">GRAND TOTAL:</td>
                <td style="text-align: right;">${calculateGrandTotal(po)}</td>
              </tr>
            </tfoot>
          </table>

          <div class="section-title">Payment Information & Confirmation</div>
          <div class="payment-section">
            <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px;">
              <div class="payment-field">
                <label>Bank Account Number:</label>
                <div class="value">${bankAccountNumber || "Not specified"}</div>
              </div>
              <div class="payment-field">
                <label>Account Name:</label>
                <div class="value">${bankAccountName || "Not specified"}</div>
              </div>
              <div class="payment-field">
                <label>Payment Method:</label>
                <div class="value">${paymentMethod || "Not specified"}</div>
              </div>
              <div class="payment-field">
                <label>Date Paid:</label>
                <div class="value" style="min-height: 32px;"></div>
              </div>
              <div class="payment-field">
                <label>Paid By:</label>
                <div class="value" style="min-height: 32px;"></div>
              </div>
            </div>
          </div>

          <div class="section-title">Additional Information</div>
          <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 20px; margin-bottom: 20px;">
            <div class="payment-section" style="margin: 0;">
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
                <div class="payment-field">
                  <label>Encoded Remarks:</label>
                  <div class="value" style="min-height: 32px; font-weight: 500;">${
                    po.remarks || ""
                  }</div>
                </div>
                <div class="payment-field">
                  <label>Notes:</label>
                  <div class="value" style="min-height: 32px;"></div>
                </div>
              </div>
            </div>
            <div style="
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              background: ${
                po.is_vat_inclusive
                  ? "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
                  : "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)"
              };
              border: 2px solid ${po.is_vat_inclusive ? "#4caf50" : "#f44336"};
              border-radius: 8px;
              padding: 16px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            ">
              <div style="
                font-size: 10px;
                color: #666;
                font-weight: 600;
                margin-bottom: 6px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              ">VAT Status</div>
              <div style="
                font-size: 16px;
                color: ${po.is_vat_inclusive ? "#2e7d32" : "#c62828"};
                font-weight: bold;
                letter-spacing: 1px;
              ">
                ${po.is_vat_inclusive ? "VAT INCLUSIVE" : "VAT EXCLUSIVE"}
              </div>
            </div>
          </div>

          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Prepared By</div>
              <div class="signature-name">Signature over Printed Name</div>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Approved By</div>
              <div class="signature-name">Signature over Printed Name</div>
            </div>
          </div>

          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()} | This is a computer-generated document</p>
          </div>
        </body>
        </html>
      `;
};

/**
 * Open print window with purchase order
 * @param {Object} po - Purchase order object
 * @param {Array} suppliers - List of suppliers
 * @param {Object} branch - Branch object with branch_name and branch_address
 */
export const printPurchaseOrder = (
  po,
  suppliers = [],
  branch = null,
  deliveryAddress = "",
  contactPerson = ""
) => {
  const printWindow = window.open("", "", "width=800,height=600");
  printWindow.document.write(
    generatePrintHTML(po, suppliers, branch, deliveryAddress, contactPerson)
  );
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

/**
 * Copy purchase order as image to clipboard
 * @param {Object} po - Purchase order object
 * @param {Array} suppliers - List of suppliers
 * @param {Object} branch - Branch object
 * @param {string} deliveryAddress - Delivery address override
 * @param {string} contactPerson - Contact person override
 * @returns {Promise<boolean>} Whether copy succeeded
 */
export const copyPurchaseOrderAsImage = async (
  po,
  suppliers = [],
  branch = null,
  deliveryAddress = "",
  contactPerson = ""
) => {
  // Build the full standalone HTML document (incl. its <style> block).
  const fullHTML = generatePrintHTML(
    po,
    suppliers,
    branch,
    deliveryAddress,
    contactPerson
  );

  // Render inside an IFRAME (a real document) rather than injecting a bare
  // <style> + <div> into the main document. html-to-image's SVG foreignObject
  // capture silently produced a blank (but correctly-sized) image for the
  // class-selector-based PO markup when rendered in the main document — the
  // iframe approach is the one that works reliably (same pattern as the
  // quotation copy-as-image). The branch was a red herring; it's the render
  // context, not the branch data.
  const container = document.createElement("div");
  container.style.cssText = "position:fixed;left:-9999px;top:0;z-index:-1;";
  document.body.appendChild(container);

  const iframe = document.createElement("iframe");
  iframe.style.cssText = "width:800px;height:auto;border:none;background:#fff;";
  container.appendChild(iframe);

  try {
    // Write the full HTML (with its <style>) into the iframe document so the
    // class selectors actually apply to the content.
    iframe.contentDocument.open();
    iframe.contentDocument.write(fullHTML);
    iframe.contentDocument.close();

    // Let the iframe lay out, then wait for its fonts.
    await new Promise((resolve) => setTimeout(resolve, 300));
    if (iframe.contentDocument.fonts && iframe.contentDocument.fonts.ready) {
      try {
        await iframe.contentDocument.fonts.ready;
      } catch (e) {
        // ignore font-loading errors; proceed with best effort
      }
    }

    const body = iframe.contentDocument.body;
    iframe.style.height = body.scrollHeight + "px";
    await new Promise((resolve) => setTimeout(resolve, 100));

    const blob = await toBlob(body, {
      backgroundColor: "#ffffff",
      pixelRatio: 2,
      width: body.scrollWidth,
      height: body.scrollHeight,
    });

    if (!blob) {
      throw new Error("Failed to generate image");
    }

    // Try Electron's native clipboard first (works reliably in Electron)
    try {
      // eslint-disable-next-line no-undef
      const { clipboard, nativeImage } = window.require("electron");
      const arrayBuffer = await blob.arrayBuffer();
      const buffer = Buffer.from(arrayBuffer);
      const image = nativeImage.createFromBuffer(buffer);
      clipboard.writeImage(image);
      return true;
    } catch (electronErr) {
      // Not in Electron (or nodeIntegration disabled) — fall back to browser API
    }

    // eslint-disable-next-line no-undef
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);

    return true;
  } finally {
    document.body.removeChild(container);
  }
};
